import { useState, useMemo, useEffect, useRef } from "react";
import { Clock, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { APP_STORE_URL, getDeviceLabel, isIOSWebBrowser, trackCtaClick } from "@/data/appLinks";
import { trackEvent } from "@/lib/productEvents";

interface LeaveByCalculatorProps {
  standardWait: number;
  precheckWait: number;
  airportCode?: string;
}

export const LeaveByCalculator = ({ standardWait, precheckWait, airportCode }: LeaveByCalculatorProps) => {
  const [flightTime, setFlightTime] = useState("");
  const [usePrecheck, setUsePrecheck] = useState(false);

  const BUFFER_MINUTES = 90;

  const leaveByTime = useMemo(() => {
    if (!flightTime) return null;

    const [hours, minutes] = flightTime.split(":").map(Number);
    if (isNaN(hours) || isNaN(minutes)) return null;

    const flightDate = new Date();
    flightDate.setHours(hours, minutes, 0, 0);

    // If time is in the past, assume tomorrow
    if (flightDate.getTime() < Date.now()) {
      flightDate.setDate(flightDate.getDate() + 1);
    }

    const waitMinutes = usePrecheck ? precheckWait : standardWait;
    const totalMinutesBefore = BUFFER_MINUTES + waitMinutes;

    const leaveDate = new Date(flightDate.getTime() - totalMinutesBefore * 60000);

    return {
      time: leaveDate.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }),
      waitUsed: waitMinutes,
    };
  }, [flightTime, usePrecheck, standardWait, precheckWait]);

  // Record once per visit that someone actually worked out when to leave.
  const trackedRef = useRef(false);
  useEffect(() => {
    if (leaveByTime && !trackedRef.current) {
      trackedRef.current = true;
      trackEvent("leave_by_used", airportCode);
    }
  }, [leaveByTime, airportCode]);

  return (
    <div className="space-y-4 px-2">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
        <div className="flex-1 w-full">
          <label htmlFor="flight-time" className="block text-sm font-medium text-muted-foreground mb-1.5">
            My flight departs at
          </label>
          <input
            id="flight-time"
            type="time"
            value={flightTime}
            onChange={(e) => setFlightTime(e.target.value)}
            className="w-full h-11 px-3 rounded-xl border border-input bg-background text-foreground text-base focus:outline-none focus:ring-2 focus:ring-ring min-h-[44px]"
          />
        </div>
        <Button
          variant={usePrecheck ? "default" : "outline"}
          size="sm"
          className="min-h-[44px] whitespace-nowrap"
          onClick={() => setUsePrecheck(!usePrecheck)}
        >
          {usePrecheck ? "✓ TSA PreCheck" : "TSA PreCheck?"}
        </Button>
      </div>

      {leaveByTime && (
        <div className="bg-primary/5 rounded-xl p-4 text-center space-y-1">
          <p className="text-sm text-muted-foreground">Leave home by</p>
          <p className="text-3xl md:text-4xl font-bold text-primary tabular-nums">
            {leaveByTime.time}
          </p>
          <p className="text-xs text-muted-foreground">
            Includes {leaveByTime.waitUsed} min security + {BUFFER_MINUTES} min buffer
          </p>
          {getDeviceLabel() !== "android" && (
            <a
              href={isIOSWebBrowser() ? APP_STORE_URL : "/app"}
              target={isIOSWebBrowser() ? "_blank" : undefined}
              rel="noopener noreferrer"
              onClick={() => trackCtaClick("leave_by_result", airportCode)}
              className="inline-flex items-center gap-1.5 justify-center min-h-[44px] text-base font-medium text-primary hover:underline pt-1"
            >
              <Bell className="h-4 w-4" aria-hidden="true" />
              Get reminded at {leaveByTime.time} — free on iPhone
            </a>
          )}
        </div>
      )}

      {!flightTime && (
        <div className="text-center py-2">
          <p className="text-sm text-muted-foreground flex items-center justify-center gap-1.5">
            <Clock className="h-3.5 w-3.5" />
            Enter your flight time to see when to leave
          </p>
        </div>
      )}
    </div>
  );
};
